<script setup lang="ts">
import { ref, computed } from 'vue';
import { useSiteService } from 'src/services/site-service';
import type { FetchSiteResponse } from 'src/api/models';

const COUNTRY_OPTIONS = [
  { label: 'Netherlands', value: 'NL' },
  { label: 'Belgium', value: 'BE' },
  { label: 'Germany', value: 'DE' },
];

const props = defineProps<{
  initialEntity?: FetchSiteResponse;
}>();

const emit = defineEmits<{
  created: [site: FetchSiteResponse];
  cancel: [];
}>();

const { createSite, fetchSite } = useSiteService();

const name = ref(props.initialEntity?.name ?? '');
const street = ref(props.initialEntity?.street ?? '');
const houseNumber = ref<number | null>(props.initialEntity?.house_number ?? null);
const houseNumberExt = ref(props.initialEntity?.house_number_extension ?? '');
const postCode = ref(props.initialEntity?.post_code ?? '');
const city = ref(props.initialEntity?.city ?? '');
const country = ref(props.initialEntity?.country ?? 'NL');
const description = ref(props.initialEntity?.description ?? '');
const isSubmitting = ref(false);

const isValid = computed(() => !!city.value.trim() && !!country.value.trim());

function resetForm() {
  name.value = '';
  street.value = '';
  houseNumber.value = null;
  houseNumberExt.value = '';
  postCode.value = '';
  city.value = '';
  country.value = 'NL';
  description.value = '';
}

async function handleSubmit() {
  if (!isValid.value) return;
  isSubmitting.value = true;
  try {
    const siteId = await createSite({
      name: name.value || null,
      street: street.value || null,
      house_number: houseNumber.value,
      house_number_extension: houseNumberExt.value || null,
      post_code: postCode.value || null,
      city: city.value,
      country: country.value,
      description: description.value || null,
    });
    if (siteId) {
      const fullSite = await fetchSite(siteId);
      emit('created', fullSite);
      resetForm();
    }
  } catch (error) {
    console.error('Failed to create site:', error);
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <div class="create-panel">
    <div class="flex items-center justify-between mb-2">
      <div class="entity-badge bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
        <i class="pi pi-map-marker" style="font-size: 11px" />
        Nieuwe locatie
      </div>
      <button class="cancel-link" @click="emit('cancel')">
        <i class="pi pi-arrow-left" style="font-size: 10px" />
        Search
      </button>
    </div>

    <div class="form-fields">
      <input
        v-model="name"
        type="text"
        class="field"
        placeholder="Site name (optional)"
      />
      <div class="grid grid-cols-6 gap-2">
        <input
          v-model="street"
          type="text"
          class="field col-span-3"
          placeholder="Street"
        />
        <input
          v-model.number="houseNumber"
          type="number"
          class="field col-span-2"
          placeholder="Number"
        />
        <input
          v-model="houseNumberExt"
          type="text"
          class="field col-span-1"
          placeholder="Ext"
        />
      </div>
      <div class="grid grid-cols-6 gap-2">
        <input
          v-model="postCode"
          type="text"
          class="field col-span-2"
          placeholder="Postcode"
        />
        <input
          v-model="city"
          type="text"
          class="field col-span-2"
          placeholder="City *"
        />
        <select v-model="country" class="field col-span-2">
          <option v-for="c in COUNTRY_OPTIONS" :key="c.value" :value="c.value">{{ c.label }}</option>
        </select>
      </div>
      <input
        v-model="description"
        type="text"
        class="field"
        placeholder="Description (optional)"
      />

      <button
        class="submit-btn"
        :disabled="!isValid || isSubmitting"
        @click="handleSubmit"
      >
        <i v-if="isSubmitting" class="pi pi-spinner pi-spin" style="font-size: 11px" />
        Toevoegen
      </button>
    </div>
  </div>
</template>

<style scoped>
.submit-btn {
  justify-content: center;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  margin-top: 0.25rem;
}
</style>
